(() => {
  const heroTimers = new WeakMap();
  let revealObserver;

  const getMenu = () => document.querySelector('[data-sv-menu]');
  const getSearch = () => document.querySelector('[data-sv-search]');

  const setHeaderState = () => {
    const header = document.querySelector('[data-sv-header]');
    if (!header) return;
    header.classList.toggle('is-solid', window.scrollY > 28 || !document.querySelector('[data-sv-slideshow]'));
  };

  const syncLayerState = () => {
    const menu = getMenu();
    const search = getSearch();
    const hasOpenLayer = (menu && !menu.hidden) || (search && !search.hidden);
    document.documentElement.classList.toggle('sv-layer-open', Boolean(hasOpenLayer));
    document.documentElement.classList.toggle('sv-menu-open', Boolean(menu && !menu.hidden));
  };

  const openLayer = (layer) => {
    if (!layer) return;
    layer.hidden = false;
    syncLayerState();
  };

  const closeLayer = (layer) => {
    if (!layer) return;
    layer.hidden = true;
    syncLayerState();
  };

  const stopHero = (hero) => {
    const timer = heroTimers.get(hero);
    if (timer) window.clearInterval(timer);
    heroTimers.delete(hero);
  };

  const setHeroSlide = (hero, nextIndex) => {
    const dots = Array.from(hero.querySelectorAll('[data-sv-slide]'));
    if (!dots.length) return;

    const index = Math.max(0, Math.min(nextIndex, dots.length - 1));
    const dot = dots[index];
    const main = hero.querySelector('[data-sv-hero-main]');
    const eyebrow = hero.querySelector('[data-sv-slide-eyebrow]');
    const title = hero.querySelector('[data-sv-slide-title]');
    const text = hero.querySelector('[data-sv-slide-text]');
    const action = hero.querySelector('[data-sv-slide-action]');

    if (main && dot.dataset.svImage) main.style.setProperty('--sv-hero-image', `url('${dot.dataset.svImage}')`);
    if (main && dot.dataset.svMobileImage) main.style.setProperty('--sv-hero-image-mobile', `url('${dot.dataset.svMobileImage}')`);
    if (eyebrow) eyebrow.textContent = dot.dataset.svEyebrow || '';
    if (title) title.textContent = dot.dataset.svTitle || '';
    if (text) {
      text.textContent = dot.dataset.svText || '';
      text.hidden = !dot.dataset.svText;
    }
    if (action) {
      action.textContent = dot.dataset.svAction || '';
      action.hidden = !dot.dataset.svAction;
      action.setAttribute('href', dot.dataset.svUrl || '#');
    }

    dots.forEach((item, dotIndex) => {
      const active = dotIndex === index;
      item.classList.toggle('active', active);
      if (active) item.setAttribute('aria-current', 'true');
      else item.removeAttribute('aria-current');
    });
    hero.dataset.svCurrentSlide = String(index);
  };

  const initHero = (hero) => {
    stopHero(hero);
    const dots = Array.from(hero.querySelectorAll('[data-sv-slide]'));
    if (!dots.length) return;

    const current = Number(hero.dataset.svCurrentSlide || 0);
    setHeroSlide(hero, Number.isFinite(current) ? current : 0);

    const autoplay = hero.dataset.svAutoplay === 'true';
    if (!autoplay || dots.length < 2 || window.Shopify?.designMode) return;

    const configuredInterval = Number(hero.dataset.svInterval || 5200);
    const interval = Number.isFinite(configuredInterval) ? Math.max(configuredInterval, 3000) : 5200;
    const timer = window.setInterval(() => {
      const index = Number(hero.dataset.svCurrentSlide || 0);
      setHeroSlide(hero, (index + 1) % dots.length);
    }, interval);
    heroTimers.set(hero, timer);
  };

  const getRevealObserver = () => {
    if (revealObserver || !('IntersectionObserver' in window)) return revealObserver;
    revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.14 });
    return revealObserver;
  };

  const initReveals = (root = document) => {
    const items = [];
    if (root.matches?.('.sv-reveal')) items.push(root);
    items.push(...root.querySelectorAll('.sv-reveal'));

    if (window.Shopify?.designMode || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    const observer = getRevealObserver();
    items.forEach((item) => observer.observe(item));
  };

  const initSections = (root = document) => {
    const heroes = [];
    if (root.matches?.('[data-sv-slideshow]')) heroes.push(root);
    heroes.push(...root.querySelectorAll('[data-sv-slideshow]'));
    heroes.forEach(initHero);
    initReveals(root);
    setHeaderState();
  };

  document.addEventListener('click', (event) => {
    const openMenu = event.target.closest('[data-sv-open-menu]');
    const closeMenu = event.target.closest('[data-sv-close-menu]');
    const openSearch = event.target.closest('[data-sv-open-search]');
    const closeSearch = event.target.closest('[data-sv-close-search]');
    const openCart = event.target.closest('[data-sv-open-cart]');
    const slideDot = event.target.closest('[data-sv-slide]');

    if (slideDot) {
      const hero = slideDot.closest('[data-sv-slideshow]');
      if (hero) {
        setHeroSlide(hero, Number(slideDot.dataset.svSlide || 0));
        if (window.Shopify?.designMode) stopHero(hero);
      }
    }

    if (openMenu) {
      event.preventDefault();
      const menu = getMenu();
      if (menu && !menu.hidden) closeLayer(menu);
      else openLayer(menu);
    }

    if (closeMenu) {
      event.preventDefault();
      closeLayer(getMenu());
    }

    if (openSearch) {
      event.preventDefault();
      closeLayer(getMenu());
      const search = getSearch();
      openLayer(search);
      window.setTimeout(() => search?.querySelector('input[type="search"]')?.focus(), 50);
    }

    if (closeSearch) {
      event.preventDefault();
      closeLayer(getSearch());
    }

    if (openCart) {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer || typeof cartDrawer.open !== 'function') return;
      event.preventDefault();
      closeLayer(getMenu());
      cartDrawer.open(openCart);
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeLayer(getMenu());
    closeLayer(getSearch());
  });

  document.addEventListener('shopify:section:load', (event) => initSections(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const heroes = event.target.matches?.('[data-sv-slideshow]')
      ? [event.target]
      : Array.from(event.target.querySelectorAll('[data-sv-slideshow]'));
    heroes.forEach(stopHero);
  });
  document.addEventListener('shopify:block:select', (event) => {
    const blockId = event.detail?.blockId;
    if (!blockId) return;
    const dot = Array.from(document.querySelectorAll('[data-sv-block-id]'))
      .find((item) => item.dataset.svBlockId === blockId);
    const hero = dot?.closest('[data-sv-slideshow]');
    if (!hero || !dot) return;
    stopHero(hero);
    setHeroSlide(hero, Number(dot.dataset.svSlide || 0));
  });

  window.addEventListener('scroll', setHeaderState, { passive: true });
  initSections();
})();
